import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FadeUp from "./FadeUp";
import { CONTACT } from "../data/content";
import { FLEET_IMAGES } from "../data/images";

const FLEET = [
  {
    label: "Sedan",
    seats: "4 Seats",
    models: "Swift Dzire, Honda Amaze",
    best: "City rides & airport transfers",
    gradient: "from-brand to-brand-dark",
    img: FLEET_IMAGES.sedan,
  },
  {
    label: "SUV",
    seats: "6–7 Seats",
    models: "Ertiga, Innova Crysta",
    best: "Family trips & rough terrain",
    gradient: "from-accent to-accent-dark",
    img: FLEET_IMAGES.suv,
  },
  {
    label: "Luxury",
    seats: "4 Seats",
    models: "Premium Sedans & Crysta",
    best: "Weddings & corporate VIPs",
    gradient: "from-navy to-navy-light",
    img: FLEET_IMAGES.luxury,
  },
  {
    label: "Traveller",
    seats: "12–26 Seats",
    models: "Traveller",
    best: "Pilgrimages & large groups",
    gradient: "from-emerald-500 to-emerald-700",
    img: FLEET_IMAGES.tempo,
  },
  {
    label: "Bus",
    seats: "30–52 Seats",
    models: "Full-size Coach Bus",
    best: "Weddings & corporate events",
    gradient: "from-purple-500 to-purple-700",
    img: FLEET_IMAGES.bus,
  },
];

export default function FleetSection() {
  const [active, setActive] = useState(0);
  const current = FLEET[active];

  return (
    <section id="fleet" className="bg-[#f4f9fb] py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <FadeUp>
          <div className="text-center">
            <p className="section-label justify-center">← Top Fleet →</p>
            <h2 className="heading-xl text-3xl sm:text-4xl">
              Pick the Right Vehicle for Your Trip
            </h2>
          </div>
        </FadeUp>

        {/* Pill tabs */}
        <FadeUp delay={0.1}>
          <div className="no-scrollbar mt-8 flex gap-3 overflow-x-auto pb-2">
            {FLEET.map((f, i) => (
              <button
                key={f.label}
                onClick={() => setActive(i)}
                className={`shrink-0 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 ${
                  active === i
                    ? "bg-accent text-white shadow-[0_10px_24px_rgba(249,115,22,0.35)]"
                    : "bg-white text-navy hover:bg-brand-tint"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </FadeUp>

        {/* Panoramic banner */}
        <FadeUp delay={0.15}>
          <div className="relative mt-6 h-80 overflow-hidden rounded-card shadow-card sm:h-96">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.label}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <img
                  src={current.img}
                  alt={`${current.label}, ${current.models}`}
                  loading="lazy"
                  className="h-full w-full object-cover object-center"
                />
                {/* Left-weighted scrim: keeps the copy legible while the
                    vehicle itself stays visible on the right. */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${current.gradient} opacity-90 mix-blend-multiply sm:bg-gradient-to-r sm:from-black/80 sm:via-black/45 sm:to-transparent sm:opacity-100 sm:mix-blend-normal`}
                />
              </motion.div>
            </AnimatePresence>

            <div className="relative flex h-full flex-col justify-center gap-3 p-8 text-white sm:p-12">
              <span className="w-fit rounded-full bg-white/20 px-4 py-1 text-xs font-semibold uppercase tracking-wide">
                {current.seats}
              </span>
              <h3 className="font-heading text-3xl font-extrabold sm:text-4xl">
                {current.label}
              </h3>
              <p className="max-w-md text-white/90">
                <span className="font-semibold">Models:</span> {current.models}
                <br />
                <span className="font-semibold">Best for:</span> {current.best}
              </p>
              <a
                href={`https://wa.me/${CONTACT.wa}?text=Hi%2C%20I%20want%20to%20hire%20a%20${encodeURIComponent(
                  current.label,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-navy transition-colors hover:bg-accent hover:text-white"
              >
                Enquire Now →
              </a>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
